import { useState, useCallback } from 'react';
import { createContainer } from 'unstated-next';
import { useDataProvider, useGetOne } from 'react-admin';
import {
  ModelDto,
  BuilderDto,
  SubdivisionDto,
  SpModelNameInterface,
} from '@vatos-pas/common';

import { ModelPayload } from '../modelPayload';

export interface ModelFilters {
  subdivisionId?: string;
  'name||$cont'?: string;
  'externalId||$cont'?: string;
  'garageFirst||$eq'?: boolean;
}

export interface ModelPagination {
  page: number;
  perPage: number;
}

export interface ModelSort {
  field: string;
  order: 'ASC' | 'DESC';
}

/**
 * Model Container - Data Access Layer
 *
 * SWR BEHAVIOR: Uses react-admin's useGetOne which caches data in Redux store.
 * Cached data is returned immediately while fresh data is fetched in the
 * background. Loading spinner only shows on first load when no cached data
 * exists. Cache persists across navigations via Redux.
 */
function useModel() {
  const dataProvider = useDataProvider();

  const [filters, setFilters] = useState<ModelFilters>({});

  const [pagination, setPagination] = useState<ModelPagination>({
    page: 0,
    perPage: 25,
  });

  const [sort, setSort] = useState<ModelSort>({
    field: 'subdivisionId',
    order: 'ASC',
  });

  const [modelId, setModelId] = useState<string>('');
  const {
    data: selectedModel,
    loading: modelLoading,
    loaded: modelLoaded,
    error: modelError,
  } = useGetOne<ModelDto>('model', modelId, { enabled: !!modelId });

  const isLoading = !!modelId && !selectedModel && modelLoading;
  const isRevalidating = !!modelId && modelLoading;

  const [formLoading, setFormLoading] = useState(false);

  const [builders, setBuilders] = useState<BuilderDto[]>([]);
  const [subdivisions, setSubdivisions] = useState<SubdivisionDto[]>([]);

  const [spModels, setSpModels] = useState<SpModelNameInterface[]>([]);
  const [spModelsLoading, setSpModelsLoading] = useState(false);

  /**
   * Load a single model by ID (triggers useGetOne via state change)
   */
  const loadModelById = useCallback((id: string): void => {
    setModelId(id);
  }, []);

  /**
   * Create a new model
   */
  const createModel = useCallback(
    async (data: ModelPayload) => {
      setFormLoading(true);
      try {
        const result = await dataProvider.create<ModelDto>('model', { data });
        setModelId(result.data.id);
        return result.data;
      } catch (err) {
        console.error('Error creating model:', err);
        throw err;
      } finally {
        setFormLoading(false);
      }
    },
    [dataProvider],
  );

  /**
   * Update an existing model
   */
  const updateModel = useCallback(
    async (id: string, data: ModelPayload) => {
      setFormLoading(true);
      try {
        const result = await dataProvider.update<ModelDto>('model', {
          id,
          data,
          previousData: selectedModel || { id },
        });
        return result.data;
      } catch (err) {
        console.error('Error updating model:', err);
        throw err;
      } finally {
        setFormLoading(false);
      }
    },
    [dataProvider, selectedModel],
  );

  /**
   * Load all builders
   */
  const loadBuilders = useCallback(async () => {
    try {
      const result = await dataProvider.getList<BuilderDto>('builder', {
        filter: {},
        pagination: { page: 1, perPage: 9999 },
        sort: { field: 'name', order: 'ASC' },
      });
      setBuilders(result.data);
      return result.data;
    } catch (err) {
      console.error('Error loading builders:', err);
      return [];
    }
  }, [dataProvider]);

  /**
   * Search builders with optional search term (for autocomplete)
   */
  const searchBuilders = useCallback(
    async (search: string) => {
      try {
        const filter: Record<string, any> = {};
        if (search) {
          filter['name||$cont'] = search;
        }

        const result = await dataProvider.getList<BuilderDto>('builder', {
          filter,
          pagination: { page: 1, perPage: 25 },
          sort: { field: 'name', order: 'ASC' },
        });
        return result.data;
      } catch (err) {
        console.error('Error searching builders:', err);
        return [];
      }
    },
    [dataProvider],
  );

  /**
   * Load subdivisions with optional builder filter and search
   */
  const loadSubdivisions = useCallback(
    async (builderId?: string, search?: string) => {
      try {
        const filter: Record<string, any> = { active: true };
        if (builderId) {
          filter.builderId = builderId;
        }
        if (search) {
          filter['name||$cont'] = search;
        }

        const result = await dataProvider.getList<SubdivisionDto>(
          'subdivision',
          {
            filter,
            pagination: { page: 1, perPage: 50 },
            sort: { field: 'name', order: 'ASC' },
          },
        );
        setSubdivisions(result.data);
        return result.data;
      } catch (err) {
        console.error('Error loading subdivisions:', err);
        return [];
      }
    },
    [dataProvider],
  );

  /**
   * Load SP models for a specific model
   */
  const loadSpModels = useCallback(
    async (modelId: string) => {
      setSpModelsLoading(true);
      try {
        const result = await dataProvider.getList<SpModelNameInterface>(
          'sp-model-name',
          {
            filter: { modelId },
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'spName', order: 'ASC' },
          },
        );
        setSpModels(result.data);
        return result.data;
      } catch (err) {
        console.error('Error loading SP models:', err);
        return [];
      } finally {
        setSpModelsLoading(false);
      }
    },
    [dataProvider],
  );

  /**
   * Create a new SP model name
   */
  const createSpModel = useCallback(
    async (
      modelId: string,
      subdivisionId: string,
      spName: string,
      active: boolean,
    ) => {
      const spNameTrimmed = spName.trim();
      if (spNameTrimmed.length === 0) {
        throw new Error('Model name cannot be empty');
      }

      try {
        const result = await dataProvider.create<SpModelNameInterface>(
          'sp-model-name',
          {
            data: {
              modelId,
              subdivisionId,
              spName: spNameTrimmed,
              active,
            },
          },
        );
        setSpModels(prev => [...prev, result.data]);
        return result.data;
      } catch (err) {
        console.error('Error creating SP model:', err);
        throw err;
      }
    },
    [dataProvider],
  );

  /**
   * Update SP model active status
   */
  const updateSpModelActive = useCallback(
    async (id: string, active: boolean) => {
      try {
        const currentSpModel = spModels.find(sp => sp.id === id);
        await dataProvider.update('sp-model-name', {
          id,
          data: { active },
          previousData: currentSpModel || { id },
        });
        setSpModels(prev =>
          prev.map(sp => (sp.id === id ? { ...sp, active } : sp)),
        );
      } catch (err) {
        console.error('Error updating SP model:', err);
        throw err;
      }
    },
    [dataProvider, spModels],
  );

  /**
   * Get builder info for a subdivision
   */
  const getBuilderForSubdivision = useCallback(
    async (subdivisionId: string) => {
      try {
        const subdivisionResult = await dataProvider.getOne<SubdivisionDto>(
          'subdivision',
          { id: subdivisionId },
        );
        if (subdivisionResult.data?.builderId) {
          const builderResult = await dataProvider.getOne<BuilderDto>(
            'builder',
            { id: subdivisionResult.data.builderId },
          );
          return builderResult.data;
        }
        return null;
      } catch (err) {
        console.error('Error getting builder for subdivision:', err);
        return null;
      }
    },
    [dataProvider],
  );

  /**
   * Update pagination
   */
  const handlePaginationChange = useCallback(
    (newPage: number, newPerPage?: number) => {
      setPagination(prev => ({
        page: newPage,
        perPage: newPerPage ?? prev.perPage,
      }));
    },
    [],
  );

  /**
   * Update sorting
   */
  const handleSortChange = useCallback((field: string) => {
    setSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'ASC' ? 'DESC' : 'ASC',
    }));
  }, []);

  /**
   * Update filters
   */
  const handleFilterChange = useCallback((newFilters: ModelFilters) => {
    setFilters(newFilters);
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({});
    setPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  return {
    // List State
    filters,
    pagination,
    sort,

    // Primary model state (SWR pattern via useGetOne + Redux)
    selectedModel,
    isLoading, // true only on first load (no cache in Redux)
    isRevalidating, // true during any fetch
    modelLoaded, // true once fetch completed (use to detect "not found")
    modelError, // truthy on fetch failure (404, network, etc.)
    // Secondary form state
    formLoading,
    builders,
    subdivisions,

    // SP Models State
    spModels,
    spModelsLoading,

    // List Actions
    handlePaginationChange,
    handleSortChange,
    handleFilterChange,
    clearFilters,

    // Form Actions
    loadModelById,
    createModel,
    updateModel,

    // Builder/Subdivision Actions
    loadBuilders,
    searchBuilders,
    loadSubdivisions,
    getBuilderForSubdivision,

    // SP Models Actions
    loadSpModels,
    createSpModel,
    updateSpModelActive,
    setSpModels,
  };
}

export const ModelContainer = createContainer(useModel);
